import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import { useToastStore } from '@/stores/toast'
import { i18n } from '@/i18n'

// Token storage keys
const PLAYER_TOKEN_KEY = 'access_token'
const ADMIN_TOKEN_KEY = 'admin_access_token'

// Player and admin tokens are stored separately so both sessions can coexist.
let accessToken: string | null = localStorage.getItem(PLAYER_TOKEN_KEY)
let adminAccessToken: string | null = localStorage.getItem(ADMIN_TOKEN_KEY)

/**
 * Set the JWT access token for player API requests.
 * Called by user store after login/register/refresh.
 */
export function setAccessToken(token: string | null): void {
  accessToken = token
  if (token) {
    localStorage.setItem(PLAYER_TOKEN_KEY, token)
  } else {
    localStorage.removeItem(PLAYER_TOKEN_KEY)
  }
}

/**
 * Clear player tokens and auth state.
 * Called by user store on logout or auth failure.
 */
export function clearTokens(): void {
  accessToken = null
  localStorage.removeItem(PLAYER_TOKEN_KEY)
}

/**
 * Set the JWT access token for admin API requests.
 * Called by admin store after login/refresh.
 */
export function setAdminAccessToken(token: string | null): void {
  adminAccessToken = token
  if (token) {
    localStorage.setItem(ADMIN_TOKEN_KEY, token)
  } else {
    localStorage.removeItem(ADMIN_TOKEN_KEY)
  }
}

/**
 * Clear admin tokens and auth state.
 * Called by admin store on logout or auth failure.
 */
export function clearAdminTokens(): void {
  adminAccessToken = null
  localStorage.removeItem(ADMIN_TOKEN_KEY)
}

/**
 * Check if a URL is an admin API endpoint.
 */
function isAdminUrl(url: string | undefined): boolean {
  return !!url && url.includes('/admin/')
}

const apiClient = axios.create({
  baseURL: '/',
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json',
  },
})

// Include CSRF token from meta tag in all requests
const csrfMeta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
if (csrfMeta) {
  apiClient.defaults.headers.common['X-CSRF-TOKEN'] = csrfMeta.content
}

// Request interceptor: attach JWT Bearer token (admin or player based on URL)
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = isAdminUrl(config.url) ? adminAccessToken : accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Refresh queue type
type FailedQueueEntry = {
  resolve: (token: string) => void
  reject: (error: unknown) => void
}

// Refresh mutex state — separate for player and admin
let isRefreshingPlayer = false
let isRefreshingAdmin = false
let failedPlayerQueue: FailedQueueEntry[] = []
let failedAdminQueue: FailedQueueEntry[] = []

function processPlayerQueue(error: unknown, token: string | null = null): void {
  failedPlayerQueue.forEach((promise) => {
    if (token) promise.resolve(token)
    else promise.reject(error)
  })
  failedPlayerQueue = []
}

function processAdminQueue(error: unknown, token: string | null = null): void {
  failedAdminQueue.forEach((promise) => {
    if (token) promise.resolve(token)
    else promise.reject(error)
  })
  failedAdminQueue = []
}

// Response interceptor: handle 401 with token refresh (player or admin)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }
    const isAdmin = isAdminUrl(originalRequest.url)
    const currentToken = isAdmin ? adminAccessToken : accessToken

    // Skip URLs that should never be refreshed
    const skipUrls = [
      'api/auth/refresh',
      'api/auth/login',
      'admin/api/auth/refresh',
      'admin/api/auth/login',
    ]

    // Only attempt refresh on 401, when we have a token, and haven't already retried
    if (
      error.response?.status !== 401 ||
      !currentToken ||
      originalRequest._retry ||
      skipUrls.some((url) => originalRequest.url?.includes(url))
    ) {
      return Promise.reject(error)
    }

    const isRefreshing = isAdmin ? isRefreshingAdmin : isRefreshingPlayer
    const queue = isAdmin ? failedAdminQueue : failedPlayerQueue

    // If a refresh is already in progress, queue this request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        queue.push({ resolve, reject })
      }).then((token) => {
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`
        } else {
          originalRequest.headers = { Authorization: `Bearer ${token}` }
        }
        return apiClient(originalRequest)
      })
    }

    originalRequest._retry = true

    if (isAdmin) {
      isRefreshingAdmin = true
    } else {
      isRefreshingPlayer = true
    }

    const refreshUrl = isAdmin ? '/admin/api/auth/refresh' : '/api/auth/refresh'
    const processQueue = isAdmin ? processAdminQueue : processPlayerQueue
    const setToken = isAdmin ? setAdminAccessToken : setAccessToken
    const clearFn = isAdmin ? clearAdminTokens : clearTokens
    const redirectUrl = isAdmin ? '/admin/login' : '/login'

    try {
      const response = await axios.post(
        refreshUrl,
        {},
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        },
      )

      const newToken: string = response.data.access_token
      setToken(newToken)
      processQueue(null, newToken)

      // Retry the original request with the new token
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`
      } else {
        originalRequest.headers = { Authorization: `Bearer ${newToken}` }
      }
      return apiClient(originalRequest)
    } catch (refreshError) {
      processQueue(refreshError, null)
      clearFn()
      window.location.href = redirectUrl
      return Promise.reject(refreshError)
    } finally {
      if (isAdmin) {
        isRefreshingAdmin = false
      } else {
        isRefreshingPlayer = false
      }
    }
  },
)

// Error response interceptor: show user-friendly toasts for common HTTP errors.
// Runs AFTER the JWT-refresh interceptor above. 401 is fully handled by the
// refresh logic (retry or redirect), so it is excluded here.
apiClient.interceptors.response.use(undefined, (error) => {
  const status: number | undefined = error.response?.status

  if (!status || status === 401) {
    return Promise.reject(error)
  }

  const toast = useToastStore()

  const t = i18n.global.t

  if (status === 422) {
    const data = error.response?.data
    if (data?.errors) {
      const messages = Object.values(data.errors as Record<string, string[]>).flat()
      toast.error(messages.slice(0, 3).join('. '))
    } else if (data?.message) {
      toast.error(data.message as string)
    } else {
      toast.error(t('errors.validation_failed'))
    }
  } else if (status === 403) {
    toast.error(t('errors.forbidden'))
  } else if (status === 429) {
    toast.warning(t('errors.rate_limited'))
  } else if (status >= 500) {
    toast.error(t('errors.server_error'))
  }

  return Promise.reject(error)
})

export default apiClient
